import React from 'react';
import { alpha, CssBaseline, GlobalStyles, ThemeProvider, createTheme } from '@mui/material';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#1f4f46', light: '#2f6a5f', dark: '#163c35' },
    secondary: { main: '#9b7047', light: '#bb8b5d', dark: '#7f5935' },
    success: { main: '#2e7d4f' },
    warning: { main: '#d68000' },
    error: { main: '#cb3a31' },
    background: { default: '#f6f1e7', paper: '#fffdf8' },
    text: { primary: '#1f1f1f', secondary: '#635d57' },
    divider: 'rgba(31,31,31,0.08)'
  },
  shape: { borderRadius: 20 },
  spacing: 8,
  typography: {
    fontFamily: '"Trebuchet MS", "Segoe UI", Arial, sans-serif',
    h3: { fontWeight: 800, letterSpacing: '-0.03em' },
    h4: { fontWeight: 800, letterSpacing: '-0.03em' },
    h5: { fontWeight: 800, letterSpacing: '-0.02em' },
    h6: { fontWeight: 800, letterSpacing: '-0.02em' },
    subtitle1: { fontWeight: 700 },
    button: { fontWeight: 700, textTransform: 'none', letterSpacing: '0.01em' }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': { boxSizing: 'border-box' },
        '::-webkit-scrollbar': { width: 10, height: 10 },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(31,79,70,0.24)',
          borderRadius: 999
        },
        '::-webkit-scrollbar-track': { backgroundColor: 'rgba(255,255,255,0.35)' }
      }
    },
    MuiContainer: {
      defaultProps: { maxWidth: 'lg' }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid rgba(31,31,31,0.08)',
          boxShadow: '0 18px 40px rgba(31,31,31,0.08)',
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(255,252,245,0.96))',
          overflow: 'hidden'
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
          '&:last-child': { paddingBottom: 24 }
        }
      }
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 46,
          borderRadius: 14,
          paddingInline: 18
        },
        contained: {
          boxShadow: '0 10px 18px rgba(31,31,31,0.12)'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
          backgroundColor: 'rgba(255,255,255,0.84)'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: '#fffdf8'
        }
      }
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(31,79,70,0.05)'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          color: '#35544f',
          borderBottom: '1px solid rgba(31,31,31,0.08)'
        },
        root: {
          borderColor: 'rgba(31,31,31,0.06)'
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 14
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 24,
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(255,252,245,0.98))'
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(135deg, #1f4f46 0%, #2b6459 100%)',
          boxShadow: '0 14px 32px rgba(20, 40, 36, 0.24)'
        }
      }
    }
  }
});

export function AppProviders({ children }) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          body: {
            background: 'radial-gradient(circle at top, #fbf6ec 0%, #f2e9d8 48%, #eadfca 100%)',
            color: theme.palette.text.primary
          },
          '#root, #escape-room-root': {
            minHeight: '100vh'
          },
          a: {
            color: 'inherit',
            textDecoration: 'none'
          },
          img: { display: 'block', maxWidth: '100%' },
          '.soft-panel': {
            border: '1px solid rgba(31,31,31,0.08)',
            borderRadius: 20,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.85), rgba(255,251,245,0.88))',
            boxShadow: '0 18px 36px rgba(31,31,31,0.06)'
          },
          '.section-title': {
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 4
          },
          '.muted-helper': {
            color: alpha('#1f1f1f', 0.66)
          }
        }}
      />
      {children}
    </ThemeProvider>
  );
}
